// Industry SEO landing page data.
// Each industry has unique copy, CTAs, meta, FAQs, and Colorado city snippets
// so every page is genuinely useful (not thin templated content).

export type IndustryKey =
  | "hvac"
  | "plumbing"
  | "roofing"
  | "electrical"
  | "landscaping"
  | "general-contractors"
  | "concrete-excavation"
  | "cleaning-services"
  | "snow-removal"
  | "pest-control"
  | "painting"
  | "fireplace-chimney"
  | "garage-doors"
  | "tree-service"
  | "law-firms"
  | "accountants-cpas"
  | "insurance-agencies"
  | "real-estate"
  | "tutors-coaches"
  | "photographers"
  | "medical-dental"
  | "chiropractic"
  | "gyms-fitness"
  | "salons-spas"
  | "restaurants-cafes"
  | "boutiques-retail"
  | "auto-repair"
  // 2026-04 expansion: legal specialties
  | "personal-injury-attorneys"
  | "criminal-defense-attorneys"
  | "family-law-attorneys"
  | "estate-planning-attorneys"
  // medical specialties
  | "dentists"
  | "orthodontists"
  | "physical-therapy"
  | "med-spas"
  | "plastic-surgeons"
  | "dermatologists"
  | "ophthalmologists"
  // commercial trades + heavy industry
  | "commercial-roofing"
  | "steel-buildings"
  | "custom-home-builders"
  | "trucking-hauling"
  | "equipment-rental"
  | "manufacturing"
  // B2B / financial
  | "franchise-businesses"
  | "mortgage-brokers"
  // 2026-05 expansion — niche local services with low agency competition
  | "dog-trainers"
  | "auto-dealers";

export type IndustryPage = {
  slug: IndustryKey;
  name: string; // e.g. "HVAC"
  shortName: string; // for inline copy (e.g. "HVAC contractor")
  category:
    | "Home Services"
    | "Professional Services"
    | "Health & Wellness"
    | "Retail, Food & Auto"
    | "Commercial & Industrial";
  metaTitle: string; // <60 chars ideally
  metaDescription: string; // <160 chars
  heroEyebrow: string;
  heroGoldWord: string; // word(s) inside heroTitle to gild
  heroTitle: string; // must include heroGoldWord verbatim
  heroDescription: string;
  pains: string[]; // 3-4 concrete problems we hear from this industry
  whatWeDo: { title: string; body: string }[]; // 3 service highlights tailored to this industry
  citySnippets: { city: string; body: string }[]; // 4 city-specific paragraphs
  faqs: { q: string; a: string }[]; // 3-5 FAQs unique to industry
  primaryCtaLabel: string;

  /**
   * OPTIONAL — ISO date (YYYY-MM-DD) of the last meaningful copy edit to this
   * industry's data. Surfaced as <lastmod> on the industry's entry in
   * /sitemap.xml so search engines re-crawl when the page actually changes.
   *
   * Bump when you edit metaTitle/metaDescription, pains, whatWeDo,
   * citySnippets, or faqs. Leave undefined for industries you haven't
   * touched recently — the sitemap falls back to INDUSTRY_DEFAULT_UPDATED.
   */
  lastUpdated?: string;
};

/**
 * Fallback lastmod for any industry without an explicit `lastUpdated`.
 * Bump when you do a site-wide template change to all industry pages.
 */
export const INDUSTRY_DEFAULT_UPDATED = "2026-04-26";

/** Latest lastmod for an industry page — used by /sitemap.xml. */
export function industryLastmod(i: IndustryPage): string {
  return i.lastUpdated ?? INDUSTRY_DEFAULT_UPDATED;
}

const COMMON_CTAS = {
  proposal: "Get a Free Proposal",
  call: "Book a Free Strategy Call",
};

export const INDUSTRY_PAGES: IndustryPage[] = [
  {
    slug: "hvac",
    name: "HVAC",
    shortName: "HVAC contractor",
    category: "Home Services",
    metaTitle: "HVAC Marketing & Web Design in Colorado | CWI",
    metaDescription:
      "More furnace repair, AC install, and tune-up calls for Colorado HVAC contractors. Local SEO, Google Maps, and websites built to ring the phone.",
    heroEyebrow: "Colorado HVAC Marketing",
    heroGoldWord: "HVAC contractor",
    heroTitle: "Websites and SEO that book real jobs for your HVAC contractor business",
    heroDescription:
      "We help Colorado HVAC companies dominate the Map Pack for furnace repair, AC install, and tune-ups — so the phone rings when temperatures swing.",
    pains: [
      "Phone goes quiet between heating and cooling seasons.",
      "Big-box franchises outranking you in the Map Pack.",
      "Website looks dated and doesn't show after-hours emergency CTA.",
      "Google Ads burning budget on the wrong service area.",
    ],
    whatWeDo: [
      {
        title: "Service-area pages that rank",
        body: "Unique pages for furnace repair, AC install, heat pumps, and mini-splits across every Colorado city you actually serve.",
      },
      {
        title: "Map Pack & review engine",
        body: "Google Business Profile optimization plus a review system that consistently brings in 5-star reviews from happy homeowners.",
      },
      {
        title: "Emergency-ready website",
        body: "Sticky click-to-call, after-hours messaging, and trust signals (NATE, EPA, BBB) that turn visitors into booked jobs.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Springs homeowners search for furnace repair the moment a Chinook pushes 70°F to 20°F overnight. We make sure your team — not the franchise downtown — shows up first.",
      },
      {
        city: "Denver Metro",
        body: "From Highlands Ranch to Aurora, Denver's HVAC market is brutally competitive. We focus on neighborhood-level SEO so you stop competing with every shop on I-25.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo and Cañon City homeowners want a contractor who picks up the phone. We build sites that reinforce that — local team, local trucks, local response.",
      },
      {
        city: "Castle Rock & Monument",
        body: "Higher-end neighborhoods like Castle Pines and Monument expect a polished site that matches the price tag of a full system replacement. We deliver that.",
      },
    ],
    faqs: [
      {
        q: "Do you work with HVAC franchises or only independents?",
        a: "Both — but we're a strong fit for independent and family-owned HVAC contractors who want to compete with the franchises without paying franchise fees.",
      },
      {
        q: "How fast can we start ranking for furnace repair in our city?",
        a: "Map Pack movement typically starts in 30-90 days. Organic rankings for competitive terms like 'furnace repair Colorado Springs' usually take 3-6 months of consistent work.",
      },
      {
        q: "Can you also run our Google Ads during peak seasons?",
        a: "Yes. We run targeted Google Ads campaigns timed for heating season, AC season, and tune-up promotions — with full transparency on spend and call tracking.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "plumbing",
    name: "Plumbing",
    shortName: "plumbing company",
    category: "Home Services",
    metaTitle: "Plumber Marketing & SEO in Colorado | CWI",
    metaDescription:
      "Get more emergency plumbing calls, drain cleaning jobs, and water heater installs. Colorado SEO, Google Maps, and websites built for plumbers.",
    heroEyebrow: "Colorado Plumber Marketing",
    heroGoldWord: "plumbing company",
    heroTitle: "Websites and SEO that ring the phone for your Colorado plumbing company",
    heroDescription:
      "We help local Colorado plumbers win emergency searches, water-heater jobs, and remodel installs — without paying HomeAdvisor for shared leads.",
    pains: [
      "Sharing every lead with three other plumbers on lead-gen platforms.",
      "Map Pack dominated by national chains with hundreds of reviews.",
      "Website doesn't make 'emergency 24/7' obvious in the first 2 seconds.",
      "Hard to compete on price for repipes and water heater swaps.",
    ],
    whatWeDo: [
      {
        title: "Emergency-first website",
        body: "Click-to-call above the fold, response-time promise, and service-area maps so urgent searches convert immediately.",
      },
      {
        title: "Local SEO for every service",
        body: "Drain cleaning, water heaters, sewer line, repipes, gas lines — each gets its own page that ranks in your Colorado service area.",
      },
      {
        title: "Reviews + reputation",
        body: "Automated review requests after every job, plus response templates so your Google profile stays at 4.8+ stars.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Old neighborhoods like Old Colorado City and Patty Jewett have aging cast-iron lines. We build content around the real problems your Springs customers actually search for.",
      },
      {
        city: "Denver Metro",
        body: "Hard water complaints, sump pump failures in Aurora, sewer line issues in older Denver neighborhoods — we target the searches that match your specialty.",
      },
      {
        city: "Pueblo",
        body: "Pueblo homeowners want a plumber who's been around — not a national franchise. We make sure your site signals 'local, family-owned, here for 15 years.'",
      },
      {
        city: "Castle Rock & Parker",
        body: "Newer construction in Castle Rock and Parker means tankless installs, water softeners, and remodel work. We tune your SEO toward the higher-ticket jobs.",
      },
    ],
    faqs: [
      {
        q: "Will you stop us from depending on HomeAdvisor and Angi?",
        a: "Yes — that's the whole point. We build organic + Map Pack channels you own, so you keep more margin per job and stop sharing leads with competitors.",
      },
      {
        q: "Can you handle commercial plumbing SEO too?",
        a: "Absolutely. Commercial plumbing has different searchers (property managers, GCs) and we build separate pages and campaigns for that audience.",
      },
      {
        q: "Do you offer call tracking?",
        a: "Yes. We set up call tracking on every channel so you can see exactly which marketing dollars produce booked jobs.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "roofing",
    name: "Roofing",
    shortName: "roofing company",
    category: "Home Services",
    metaTitle: "Roofing SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Colorado roofing marketing that wins post-storm and insurance claim searches. Local SEO, Google Maps, and high-converting roofer websites.",
    heroEyebrow: "Colorado Roofing Marketing",
    heroGoldWord: "roofing company",
    heroTitle: "Websites and SEO that fill the schedule for your Colorado roofing company",
    heroDescription:
      "Hail season turns Colorado into the most competitive roofing market in America. We build sites and SEO that win against the storm chasers.",
    pains: [
      "Out-of-state storm chasers crowding the Map Pack after every hail event.",
      "Insurance claim education buried instead of front-and-center.",
      "Website doesn't differentiate you from the 200 other 'roofing companies'.",
      "PPC costs spiking to $40+ per click in peak season.",
    ],
    whatWeDo: [
      {
        title: "Storm + insurance content",
        body: "Pages on hail damage, insurance claims, deductibles, and roof inspections — the questions Colorado homeowners actually ask after a storm.",
      },
      {
        title: "Local trust signals",
        body: "GAF/Owens Corning certifications, BBB, manufacturer warranties, and real Colorado job photos surfaced where they convert.",
      },
      {
        title: "Map Pack for every city",
        body: "Service-area pages and a tightly optimized Google Business Profile so locals find YOU instead of the storm chaser with a 720 area code.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Springs gets hammered by hail almost every June. We pre-build the storm response pages so the moment a storm hits, your site is already ranking.",
      },
      {
        city: "Denver Metro",
        body: "Highlands Ranch, Centennial, and Aurora are constant battlegrounds. We focus on neighborhood-level SEO and reviews to outrank the chasers.",
      },
      {
        city: "Pueblo",
        body: "Pueblo customers value a local roofer they can call back in five years for a warranty claim. We make that the centerpiece of your messaging.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Premium homes in Castle Pines and The Pinery need a polished site that matches the $30k+ replacement ticket. We design for that buyer.",
      },
    ],
    faqs: [
      {
        q: "Can you help us rank before hail season hits?",
        a: "Yes — and you should start at least 90-120 days before peak season. Pre-building authority means you're already in the Map Pack when the calls flood in.",
      },
      {
        q: "Do you handle Google Local Service Ads (LSA) too?",
        a: "Yes. We set up and optimize LSA, Google Ads, and organic SEO together so they reinforce each other instead of cannibalizing.",
      },
      {
        q: "What about commercial roofing?",
        a: "Different buyer, different content. We build separate commercial roofing pages targeting property managers, HOAs, and GCs.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "electrical",
    name: "Electrical",
    shortName: "electrical contractor",
    category: "Home Services",
    metaTitle: "Electrician Marketing & SEO in Colorado | CWI",
    metaDescription:
      "Colorado electrician SEO and websites that book panel upgrades, EV chargers, and service calls. Map Pack rankings and lead-gen done right.",
    heroEyebrow: "Colorado Electrician Marketing",
    heroGoldWord: "electrical contractor",
    heroTitle: "Websites and SEO that book real jobs for your Colorado electrical contractor",
    heroDescription:
      "EV chargers, panel upgrades, whole-home generators — we build pages and campaigns around the high-ticket work Colorado electricians actually want.",
    pains: [
      "Booked solid with low-margin service calls, starving for upgrade work.",
      "Map Pack flooded by handyman services pretending to do electrical.",
      "Website doesn't communicate licensing/insurance fast enough.",
      "Missing out on EV charger and solar interconnect demand.",
    ],
    whatWeDo: [
      {
        title: "High-ticket service pages",
        body: "Dedicated pages for EV charger install, 200A panel upgrades, generators, and solar interconnect — the work that actually moves the bottom line.",
      },
      {
        title: "Licensed-pro trust signals",
        body: "Master electrician credentials, insurance, permits, and warranty info surfaced prominently so customers stop comparing you to handymen.",
      },
      {
        title: "Local SEO + Map Pack",
        body: "Optimized service-area pages plus a Google Business Profile that ranks across your full Colorado footprint.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "EV adoption in Springs is climbing fast — Tesla and Rivian owners need Level 2 chargers. We make sure they find your shop first.",
      },
      {
        city: "Denver Metro",
        body: "Older Denver neighborhoods need panel upgrades constantly. We build localized content around the real electrical issues homeowners search for.",
      },
      {
        city: "Boulder & Longmont",
        body: "Solar interconnect, battery backup, and high-end residential are huge in Boulder County. We target the buyers who care about quality over price.",
      },
      {
        city: "Castle Rock & Parker",
        body: "New construction in Castle Rock means generators, smart-home wiring, and outbuilding circuits. We point your SEO toward those higher-margin jobs.",
      },
    ],
    faqs: [
      {
        q: "Can you separate residential from commercial electrical SEO?",
        a: "Yes. We build distinct page sets for residential vs. commercial because the searcher and the keywords are very different.",
      },
      {
        q: "Do you help with EV charger lead generation specifically?",
        a: "Yes — this is one of the fastest-growing search categories in Colorado. We build dedicated EV charger pages and Google Ads campaigns around it.",
      },
      {
        q: "Is monthly SEO worth it for a 2-truck shop?",
        a: "Often yes. We'll be honest if it's not — sometimes a smaller shop is better off with one great website + a focused Google Ads spend instead.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "landscaping",
    name: "Landscaping",
    shortName: "landscaping company",
    category: "Home Services",
    metaTitle: "Landscaping Marketing & SEO in Colorado | CWI",
    metaDescription:
      "Fill your spring schedule with landscape design, lawn care, and hardscape leads across Colorado. SEO, Maps, and websites that convert.",
    heroEyebrow: "Colorado Landscaping Marketing",
    heroGoldWord: "landscaping company",
    heroTitle: "Websites and SEO that fill the spring book for your Colorado landscaping company",
    heroDescription:
      "From xeriscaping to retaining walls to weekly maintenance, we build sites that show your work and rank for the searches that drive real revenue.",
    pains: [
      "Spring rush comes and goes before your phone catches up.",
      "Website photos don't do justice to the work you actually deliver.",
      "Hard to rank above the maintenance-only outfits when you do design/build.",
      "Off-season cash flow drops because nothing is bringing in winter inquiries.",
    ],
    whatWeDo: [
      {
        title: "Portfolio-driven website",
        body: "A website built around your project gallery — because in landscaping, the work sells itself when it's photographed and laid out properly.",
      },
      {
        title: "Service-specific SEO",
        body: "Dedicated pages for design/build, hardscape, irrigation, xeriscaping, and weekly maintenance so each service ranks on its own merit.",
      },
      {
        title: "Year-round funnel",
        body: "Snow removal, holiday lighting, and winter planning content keep inquiries coming in even in January.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Springs homeowners want xeriscape ideas that survive Black Forest winters and west-side wind. We build content around the realities of growing here.",
      },
      {
        city: "Denver Metro",
        body: "Front yard turf replacement is exploding under Denver Water rebates. We make sure your design/build pages capture that demand.",
      },
      {
        city: "Boulder & Longmont",
        body: "Native plantings, rain gardens, and sustainable design are table stakes in Boulder County. We position your shop as the local expert.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Larger lots in Castle Rock and Parker mean bigger jobs — patios, outdoor kitchens, retaining walls. We focus your SEO on those projects.",
      },
    ],
    faqs: [
      {
        q: "Should I bother with SEO if I'm fully booked every spring?",
        a: "Yes, if your goal is better jobs at better margins. SEO lets you stop chasing every $200 cleanup and book higher-value design/build work instead.",
      },
      {
        q: "Can you do paid ads alongside SEO?",
        a: "Yes — we often run Google Ads for design/build leads in early spring while SEO catches up.",
      },
      {
        q: "Do you do video and photo content?",
        a: "We don't shoot video on-site, but we'll guide you on what to capture and how to use it on the site for maximum ranking and conversion impact.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "general-contractors",
    name: "General Contractors",
    shortName: "general contractor",
    category: "Home Services",
    metaTitle: "General Contractor Marketing in Colorado | CWI",
    metaDescription:
      "Land remodel, addition, and ADU projects across Colorado. SEO, websites, and Maps for general contractors who want better-fit clients.",
    heroEyebrow: "Colorado General Contractor Marketing",
    heroGoldWord: "general contractor",
    heroTitle: "Websites and SEO that bring better-fit clients to your Colorado general contractor",
    heroDescription:
      "Remodels, additions, ADUs — we build sites that screen for the projects you actually want and weed out the price-shoppers.",
    pains: [
      "Inbox full of $5k jobs when you want $80k+ remodels.",
      "Lead-gen platforms sending unqualified, price-only shoppers.",
      "Website doesn't reflect the quality of your past work.",
      "Hard to differentiate from every other 'remodeler' in town.",
    ],
    whatWeDo: [
      {
        title: "Project-tier filtering",
        body: "Pricing transparency, process pages, and qualification copy so the bad-fit leads disqualify themselves before they ever reach you.",
      },
      {
        title: "Portfolio + case studies",
        body: "Per-project case studies with photos, scope, timeline, and budget range — the kind of content high-ticket clients actually look for.",
      },
      {
        title: "Specialized SEO",
        body: "Dedicated pages for kitchen remodels, basement finishes, ADUs, and additions so you rank for the project types you specialize in.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "ADU and basement-finish demand in Springs is steady. We build content around the permit realities and HOA constraints local buyers search for.",
      },
      {
        city: "Denver Metro",
        body: "Pop-tops, garden-level conversions, and full kitchen remodels dominate Denver. We focus your SEO on the projects with the best margins.",
      },
      {
        city: "Boulder",
        body: "Boulder remodels are high-end and code-heavy. We position your firm around quality, sustainability, and energy performance — not lowest bid.",
      },
      {
        city: "Castle Rock & Highlands Ranch",
        body: "Newer luxury homes mean kitchen and primary-bath remodels. We tune your messaging and SEO toward that buyer.",
      },
    ],
    faqs: [
      {
        q: "Can you screen out price-only leads?",
        a: "Yes — that's a big part of what good website copy does. We use minimum-budget callouts, process detail, and design polish to attract serious clients only.",
      },
      {
        q: "Do you publish project pricing on the site?",
        a: "We strongly recommend price ranges or starting-at numbers. It saves you and the client time and dramatically improves lead quality.",
      },
      {
        q: "Do you build for design-build firms specifically?",
        a: "Yes. Design-build firms need a different content approach (process-heavy, designer profiles, in-house showroom) and we build for that.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "law-firms",
    name: "Law Firms",
    shortName: "law firm",
    category: "Professional Services",
    metaTitle: "Law Firm SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Colorado law firm marketing that wins higher-value cases. Practice-area SEO, websites, and reputation management for serious attorneys.",
    heroEyebrow: "Colorado Law Firm Marketing",
    heroGoldWord: "law firm",
    heroTitle: "Websites and SEO that win better cases for your Colorado law firm",
    heroDescription:
      "Personal injury, family law, criminal defense, estate planning — we build firm websites and SEO around the practice areas and clients you want most.",
    pains: [
      "Lead quality is poor — most calls aren't a fit for your practice.",
      "Map Pack dominated by big PI mills with national TV budgets.",
      "Website looks like every other firm's template.",
      "Reviews and reputation are stagnant.",
    ],
    whatWeDo: [
      {
        title: "Practice-area SEO",
        body: "Dedicated, deeply written pages for each practice area — the kind of pages Google rewards and serious clients actually read.",
      },
      {
        title: "Authority + EEAT signals",
        body: "Attorney bios, case results (within ethics rules), bar associations, publications, and speaking — the trust signals that close consultations.",
      },
      {
        title: "Reputation engine",
        body: "Ethical, compliant review-generation flows so happy clients leave 5-star reviews on Google and Avvo without prompting awkwardness.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "El Paso County family law and DUI defense are competitive but winnable with the right content depth. We build pages that actually answer the questions clients are searching.",
      },
      {
        city: "Denver Metro",
        body: "Denver firms compete with national PI advertisers. We focus your strategy on the case types and neighborhoods where you can realistically win.",
      },
      {
        city: "Boulder",
        body: "Estate planning, business law, and family law in Boulder skew sophisticated. We match your content to that more deliberate buyer.",
      },
      {
        city: "Pueblo",
        body: "Pueblo clients value local firms with local roots. We make that the heart of your positioning instead of trying to look like a Denver mega-firm.",
      },
    ],
    faqs: [
      {
        q: "Are you familiar with state bar advertising rules?",
        a: "Yes. We work within Colorado bar advertising rules — no guarantees, proper disclaimers on case results, and ethical review-generation practices.",
      },
      {
        q: "Can you help us focus on a specific practice area?",
        a: "Yes — and we usually recommend it. Ranking for one practice area deeply beats trying to rank generically for 'lawyer near me'.",
      },
      {
        q: "Do you do PPC and LSA for attorneys?",
        a: "Yes. Local Service Ads can be very effective for certain practice areas and we manage them alongside organic SEO.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.call,
  },
  {
    slug: "medical-dental",
    name: "Medical & Dental",
    shortName: "medical or dental practice",
    category: "Health & Wellness",
    metaTitle: "Dentist & Medical Practice SEO in Colorado | CWI",
    metaDescription:
      "Fill the schedule for your Colorado dental or medical practice. Patient-acquisition websites, local SEO, and reputation done right.",
    heroEyebrow: "Colorado Medical Practice Marketing",
    heroGoldWord: "medical or dental practice",
    heroTitle: "Websites and SEO that book new patients for your Colorado medical or dental practice",
    heroDescription:
      "From general dentistry to ortho to med spas to specialty clinics, we build patient-acquisition systems that grow chair-time and procedure volume.",
    pains: [
      "Recall and referrals slowing down as the schedule thins out.",
      "Map Pack outranked by corporate dental groups.",
      "Website doesn't communicate insurance, financing, and new-patient offers clearly.",
      "Reputation feels stuck around 4.4-4.6 stars.",
    ],
    whatWeDo: [
      {
        title: "New-patient website",
        body: "Insurance acceptance, financing options, online booking, and new-patient offers placed where they actually convert mobile visitors.",
      },
      {
        title: "Procedure-level SEO",
        body: "Implants, Invisalign, veneers, sedation, etc. — each gets a dedicated page that ranks for the right intent in your city.",
      },
      {
        title: "Reviews + recall",
        body: "Automated review requests timed to the right post-visit moment, plus reactivation sequences for inactive patients.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Springs is dense with corporate dental brands. Independent practices win by going deep on procedure pages and surfacing real local reviews.",
      },
      {
        city: "Denver Metro",
        body: "Cosmetic, implant, and ortho searches in Denver are some of the most competitive in Colorado. We focus on neighborhood-specific Map Pack wins.",
      },
      {
        city: "Boulder",
        body: "Holistic, integrative, and cosmetic dentistry skew well in Boulder. We position your practice for the patient who chooses care, not just price.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Family dentistry and ortho convert strongly in Castle Rock and Parker. We tune your schedule-fillers around school-year and FSA-end timing.",
      },
    ],
    faqs: [
      {
        q: "Are you HIPAA-aware in your marketing setup?",
        a: "Yes. We avoid PHI in tracking, configure forms safely, and use BAA-compliant tools where appropriate.",
      },
      {
        q: "Can you handle multi-location practices?",
        a: "Yes. We build location-specific pages and Google Business Profiles so each office ranks in its own city.",
      },
      {
        q: "Do you work with specialists (ortho, perio, oral surgery)?",
        a: "Yes — and these benefit a lot from focused, procedure-deep SEO instead of generic 'dentist near me' content.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.call,
  },
  {
    slug: "restaurants-cafes",
    name: "Restaurants & Cafés",
    shortName: "restaurant or café",
    category: "Retail, Food & Auto",
    metaTitle: "Restaurant Marketing & Web Design in Colorado | CWI",
    metaDescription:
      "Drive more reservations, online orders, and foot traffic to your Colorado restaurant or café. Websites, Google Maps, and local SEO that work.",
    heroEyebrow: "Colorado Restaurant Marketing",
    heroGoldWord: "restaurant or café",
    heroTitle: "Websites and Google Maps that fill seats at your Colorado restaurant or café",
    heroDescription:
      "Menus, reservations, online orders, and beautiful photography — we build sites that turn 'hungry near me' searches into customers walking in the door.",
    pains: [
      "Yelp and Google photos don't reflect what the food actually looks like.",
      "Online ordering tied up in third-party fees that crush margins.",
      "Website is slow on mobile when most search happens on phones.",
      "Map Pack outranked by chains and franchises.",
    ],
    whatWeDo: [
      {
        title: "Mobile-first restaurant site",
        body: "Fast-loading, photo-forward sites with menu, hours, reservations, and online ordering — built for the person standing on the sidewalk deciding where to eat.",
      },
      {
        title: "Google Business Profile mastery",
        body: "Photos, posts, menu sync, Q&A, and review responses so your Map Pack listing dominates the 'near me' moment.",
      },
      {
        title: "Direct ordering + reservations",
        body: "We help cut third-party order fees by integrating direct ordering and reservation tools that pay for themselves fast.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Downtown, Old Colorado City, Briargate — we build content around the neighborhood your restaurant sits in, not just 'restaurants Colorado Springs'.",
      },
      {
        city: "Denver Metro",
        body: "RiNo, LoHi, Cherry Creek — Denver dining is hyper-neighborhood. Your SEO and Map Pack strategy needs to match that reality.",
      },
      {
        city: "Boulder",
        body: "Pearl Street competition is fierce. We focus on differentiation — chef story, sourcing, atmosphere — instead of trying to win on generic terms.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Family-friendly suburbs reward restaurants that nail Google photos, kid-menu visibility, and reliable online ordering.",
      },
    ],
    faqs: [
      {
        q: "Can you integrate Toast / Square / Resy / OpenTable?",
        a: "Yes — we integrate the POS and reservation systems you already use so the website becomes the front door, not a duplicate.",
      },
      {
        q: "How do we get better Google photos?",
        a: "We coach you on what to capture and how often to post. Restaurants that post fresh photos weekly outperform those that don't, every time.",
      },
      {
        q: "Do you help with delivery / direct-order strategy?",
        a: "Yes. We help reduce dependence on DoorDash/Uber Eats by building direct ordering channels that protect margin.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "auto-repair",
    name: "Auto Repair & Detailing",
    shortName: "auto shop",
    category: "Retail, Food & Auto",
    metaTitle: "Auto Repair Shop Marketing in Colorado | CWI",
    metaDescription:
      "Book more bays for your Colorado auto repair shop. Local SEO, Google Maps, and websites built for shops, detailers, and tire stores.",
    heroEyebrow: "Colorado Auto Shop Marketing",
    heroGoldWord: "auto shop",
    heroTitle: "Websites and SEO that book bays for your Colorado auto shop",
    heroDescription:
      "Repair, tires, alignment, detailing, mobile mechanic — we build local SEO and websites that turn searchers into appointments.",
    pains: [
      "Dealerships and franchises crowd the Map Pack on common service searches.",
      "Website doesn't make booking an appointment obvious.",
      "Reviews are decent but not converting — prospects still call dealerships.",
      "Specialty work (EV, diesel, European) buried instead of featured.",
    ],
    whatWeDo: [
      {
        title: "Service-specific pages",
        body: "Brakes, alignment, AC service, tires, diesel, EV — each service gets a page that targets the right searcher in your service area.",
      },
      {
        title: "Booking-first website",
        body: "Online appointment scheduling, financing, warranty info, and customer photos placed exactly where they nudge people to commit.",
      },
      {
        title: "Map Pack & reviews",
        body: "Optimized Google Business Profile, fresh photos, and a steady review stream so you outrank dealership service centers.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Military families in Springs need shops they can trust on a tight timeline. We build content that signals honesty, fair pricing, and quick turnarounds.",
      },
      {
        city: "Denver Metro",
        body: "EV and hybrid service searches are climbing fast in Denver. We make sure your specialty capabilities aren't buried three clicks deep.",
      },
      {
        city: "Pueblo",
        body: "Pueblo customers want a shop that's been around. We frame your shop's local history front-and-center.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Higher-end vehicles in Castle Rock mean European service and detailing demand. We tune SEO toward those higher-margin services.",
      },
    ],
    faqs: [
      {
        q: "Will you help us compete with dealership service centers?",
        a: "Yes — and you can absolutely win. Dealerships are bad at local SEO. A focused, well-optimized independent shop beats them on most service searches.",
      },
      {
        q: "Can you set up online appointment booking?",
        a: "Yes. We integrate the scheduling tool that fits your workflow so the website actually fills the bay calendar.",
      },
      {
        q: "Do you do detailing-only shops?",
        a: "Yes. Detailing has its own buyer (image, photos, packages, gift cards) and we build for that specifically.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  /* ============================================================
     2026-04 EXPANSION — legal & medical specialties, commercial
     trades, heavy industry, and B2B verticals.
     Each entry is genuinely category-specific (not a copy/paste
     of a parent industry) so the SEO surface area is real.
  ============================================================ */

  {
    slug: "personal-injury-attorneys",
    name: "Personal Injury Attorneys",
    shortName: "personal injury firm",
    category: "Professional Services",
    metaTitle: "Personal Injury Attorney SEO & Web Design | Colorado",
    metaDescription:
      "Marketing for Colorado personal injury law firms — auto accident, slip-and-fall, and wrongful death cases. SEO and websites tuned for high-CPC, high-stakes intake.",
    heroEyebrow: "Colorado Personal Injury Marketing",
    heroGoldWord: "personal injury firm",
    heroTitle: "Cases that fit your firm — not lead-gen leftovers — for your Colorado personal injury firm",
    heroDescription:
      "Colorado PI is one of the most expensive verticals on Google. We build organic + Map Pack channels you own, plus PPC tuned for buyer-intent calls — not tire-kicker forms.",
    pains: [
      "PPC costs running $90+ per click in Denver and the metro.",
      "Lead-gen platforms (Lawyers.com, Avvo, etc.) selling the same case to three firms.",
      "Site reads like every other PI firm — same hero, same stock photo, same '24/7 free consult'.",
      "Spanish-speaking auto accident searchers going to firms with bilingual landing pages.",
    ],
    whatWeDo: [
      {
        title: "Practice-area depth, not a hub page",
        body: "Dedicated pages for auto accident, motorcycle, truck/CDL, slip-and-fall, dog bite, and wrongful death — each tuned to that case type's actual search intent.",
      },
      {
        title: "Case-result transparency",
        body: "Schema-marked verdicts and settlements with real client narratives (no 'every case is different' boilerplate). Bar-compliant and conversion-focused.",
      },
      {
        title: "Bilingual + intake-ready",
        body: "Spanish landing pages for auto accident searches, plus call-tracked intake forms wired to your case management system so nothing falls through.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver auto accident PPC tops $90/click. We win on organic and Map Pack instead — neighborhood pages for Aurora, Lakewood, Westminster, and the I-25/I-70 corridors where the wrecks actually happen.",
      },
      {
        city: "Colorado Springs",
        body: "Springs PI is dominated by 4-5 firms with massive billboard spend. We help boutique firms win the long-tail (motorcycle, CDL truck, pedestrian) where competition is thinner and CPL is realistic.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo PI is underserved online. A focused 90-day push with bilingual content and Map Pack work usually opens up real case flow without paying lead-gen platforms.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland PI buyers research heavily before calling. We build deep practice-area content and case-result pages that hold up to that scrutiny.",
      },
    ],
    faqs: [
      {
        q: "Do you understand bar advertising rules in Colorado?",
        a: "Yes. We follow Colorado Rules of Professional Conduct (esp. Rule 7.1–7.5) on case results, testimonials, and disclaimers. Every page we ship is reviewed against current bar guidance.",
      },
      {
        q: "Can you replace our Avvo / Lawyers.com / Justia spend?",
        a: "Yes — that's usually the explicit goal. We build organic and Map Pack channels you own, then phase down lead-gen spend as your in-house intake comes online.",
      },
      {
        q: "Will you build out Spanish landing pages?",
        a: "Yes. Bilingual auto-accident pages are some of the highest-converting work we do for Colorado PI firms, especially in Aurora, Westminster, and Pueblo.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "criminal-defense-attorneys",
    name: "Criminal Defense Attorneys",
    shortName: "criminal defense firm",
    category: "Professional Services",
    metaTitle: "Criminal Defense Attorney SEO & Web Design | Colorado",
    metaDescription:
      "SEO and websites for Colorado criminal defense lawyers — DUI, felony, domestic violence, and federal cases. Built for urgent, after-hours intake.",
    heroEyebrow: "Colorado Criminal Defense Marketing",
    heroGoldWord: "criminal defense firm",
    heroTitle: "Urgent calls from real clients for your Colorado criminal defense firm",
    heroDescription:
      "Criminal defense buyers search at 2am, the day after an arrest, or right before an arraignment. We build sites and SEO that capture them in those exact moments.",
    pains: [
      "Site doesn't surface 'after-hours' or 'jail call' phone access.",
      "No dedicated pages for DUI, felony, domestic violence, or federal — just one generic 'criminal defense' page.",
      "Map Pack dominated by 'discount' DUI mills, not real trial attorneys.",
      "Reviews trapped on Avvo where they don't influence Google rankings.",
    ],
    whatWeDo: [
      {
        title: "Charge-specific landing pages",
        body: "DUI, DWAI, felony assault, domestic violence, drug possession, theft, federal — each charge type ranks separately and converts on its own intent.",
      },
      {
        title: "After-hours intake design",
        body: "Sticky click-to-call, 'we answer calls from jail' messaging, and intake forms wired for the urgency of an arrest call.",
      },
      {
        title: "Trust signals that beat the mills",
        body: "Trial wins, jury verdicts, bar discipline clean records, and real client narratives surfaced where DUI-mill firms can't compete.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver DUI is brutally competitive — 30+ firms bid on the keyword. We win on charge-specific pages (felony DUI, vehicular assault, federal) where mills don't bother to compete.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military DUI and Article 134 case flow from Fort Carson, Peterson, and Schriever. We build dedicated UCMJ + civilian-court pages that capture both.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Greeley DUI cases skew younger (CU/CSU/UNC). We build content tuned to first-offense, college-age clients and their parents who actually pay.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo and Cañon City have heavy felony and drug-possession case flow. Less crowded SERPs mean a focused content push moves rankings fast.",
      },
    ],
    faqs: [
      {
        q: "Can you handle SEO for federal criminal defense too?",
        a: "Yes. Federal practice (white-collar, drug conspiracy, immigration crime) has a totally different buyer than state DUI — we build separate funnels for each.",
      },
      {
        q: "Will you respect Colorado bar rules on case results?",
        a: "Yes. We follow Rule 7.1 disclaimers and avoid any language that implies a guaranteed outcome. Every page is reviewed before launch.",
      },
      {
        q: "Can you set up after-hours call routing?",
        a: "Yes. We integrate call-tracking with your answering service so every after-hours call is logged, attributed to the right campaign, and never dropped.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "family-law-attorneys",
    name: "Family Law Attorneys",
    shortName: "family law firm",
    category: "Professional Services",
    metaTitle: "Family Law Attorney SEO & Web Design | Colorado",
    metaDescription:
      "Marketing for Colorado family law firms — divorce, custody, mediation, and prenup work. Empathetic websites and SEO that convert research-heavy buyers.",
    heroEyebrow: "Colorado Family Law Marketing",
    heroGoldWord: "family law firm",
    heroTitle: "The right clients, at the right moment, for your Colorado family law firm",
    heroDescription:
      "Family law buyers spend weeks researching before they call. We build content and SEO that earn their trust during that research window — so when they call, they're ready to retain.",
    pains: [
      "Site reads cold, transactional, or scary instead of empathetic and competent.",
      "No content separating contested divorce, uncontested, mediation, custody, or modifications.",
      "Custody and child support searches converting to lead-gen platforms instead of your firm.",
      "High-asset and prenup work going to firms with more polished sites.",
    ],
    whatWeDo: [
      {
        title: "Practice-depth content",
        body: "Separate ranking pages for divorce, contested vs. uncontested, mediation, custody, support, modifications, and high-asset cases.",
      },
      {
        title: "Empathetic, expertise-forward design",
        body: "Visual tone that reads competent and human — not law-firm-bunker or stock-photo cheesy. Built around the emotional reality of family law buyers.",
      },
      {
        title: "Research-cycle SEO",
        body: "Long-form content answering the questions people Google before they're ready to call — Colorado-specific (CRS Title 14, parenting time guidelines, separation requirements).",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver family law is competitive but research-heavy. Buyers in Cherry Creek, Highlands, and DTC compare 4-5 firms before they call. We build content depth that wins that comparison.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military divorce and custody (USFSPA, BAH math, deployment custody plans). We build dedicated military-family content that civilian-only firms can't match.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland skew higher-asset and more mediation-friendly. We tune content to collaborative divorce and high-net-worth property division.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Douglas County family law buyers are research-heavy and price-tolerant for quality. We build polished sites that match the price tag of premium family law work.",
      },
    ],
    faqs: [
      {
        q: "Will you build a military divorce / USFSPA section?",
        a: "Yes. Military divorce is a high-value Colorado niche (Springs especially) and we build dedicated content for USFSPA, deployment custody, BAH calculation, and military pension division.",
      },
      {
        q: "Can you handle SEO for collaborative divorce + mediation specifically?",
        a: "Yes. Mediation and collaborative divorce have totally different buyers (and price points) than litigation. We build separate funnels and content for each.",
      },
      {
        q: "Do you handle review management for sensitive cases?",
        a: "Yes. We set up review systems that protect client privacy, comply with bar rules, and still build the review velocity Google needs to rank you.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "estate-planning-attorneys",
    name: "Estate Planning Attorneys",
    shortName: "estate planning firm",
    category: "Professional Services",
    metaTitle: "Estate Planning Attorney SEO & Web Design | Colorado",
    metaDescription:
      "Marketing for Colorado estate planning lawyers — wills, trusts, probate, and elder law. Trust-building websites and SEO for research-heavy, high-net-worth clients.",
    heroEyebrow: "Colorado Estate Planning Marketing",
    heroGoldWord: "estate planning firm",
    heroTitle: "High-trust clients who book real planning work for your Colorado estate planning firm",
    heroDescription:
      "Estate planning buyers want competence, longevity, and clarity — not flashy marketing. We build websites and SEO that read like the firm a serious client wants to hire.",
    pains: [
      "Site reads template-y and doesn't differentiate from every other 'wills + trusts' firm.",
      "No content separating revocable trust, irrevocable, asset protection, probate, or elder law.",
      "Probate work going to firms that show up first on 'Colorado probate attorney' searches.",
      "Younger families with young kids never find you because content targets retirees only.",
    ],
    whatWeDo: [
      {
        title: "Plan-type-specific pages",
        body: "Dedicated ranking pages for wills, revocable trusts, irrevocable trusts, probate, special needs trusts, and asset protection — each tuned to that buyer's intent.",
      },
      {
        title: "Trust-building design",
        body: "Long-tenure cues, real attorney bios, professional photography, and the credibility signals (super-lawyers, AV-rated, ACTEC) that high-net-worth buyers look for.",
      },
      {
        title: "Two-buyer content strategy",
        body: "Separate funnels for younger families (first-will, guardianship, life insurance trust) and retirees (asset protection, probate, elder law) — same firm, two clear paths.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver estate planning is heavy in Cherry Creek, Wash Park, and DTC where high-net-worth buyers compare 3-4 firms before booking. Site polish and ACTEC-level credibility win the comparison.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has strong military retiree planning (USAA, military pension, Tricare-coordinated estate work) plus growing high-net-worth in Broadmoor and Black Forest.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland have a strong charitable-trust and family-foundation buyer profile. We tune content for charitable remainder trusts and donor-advised fund integration.",
      },
      {
        city: "Castle Rock & Centennial",
        body: "Castle Rock, Castle Pines, and Centennial buyers are some of the most asset-protection focused in Colorado. We build content for irrevocable trusts, LLC layering, and inheritance planning at scale.",
      },
    ],
    faqs: [
      {
        q: "Will you build out probate-specific content separately from estate planning?",
        a: "Yes. Probate buyers (often the family of someone who just died) have totally different intent than someone planning their estate. We build separate funnels for each.",
      },
      {
        q: "Can you handle elder law and Medicaid planning content too?",
        a: "Yes. Elder law and Medicaid asset-protection planning are some of the highest-value niches in Colorado estate work. We build dedicated, regulation-current content for both.",
      },
      {
        q: "Will the site work for multi-attorney firms vs. solos?",
        a: "Yes. We build attorney-bio depth, practice-area routing, and intake systems that scale from a 1-attorney shop to a 10+ attorney firm.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "dentists",
    name: "Dentists",
    shortName: "dental practice",
    category: "Health & Wellness",
    metaTitle: "Dentist SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado general, cosmetic, and implant dentists. Insurance-aware, procedure-specific SEO and websites that book new patients consistently.",
    heroEyebrow: "Colorado Dental Marketing",
    heroGoldWord: "dental practice",
    heroTitle: "New patient flow that fills the schedule for your Colorado dental practice",
    heroDescription:
      "We help Colorado dentists rank for the procedures that actually pay — implants, Invisalign, veneers, and crowns — plus the insurance and 'dentist near me' searches new patients use.",
    pains: [
      "Site reads like every other dental template (toothbrush hero, family photo, 'gentle dentistry' headline).",
      "Implants, Invisalign, and veneers don't have their own ranking pages.",
      "New-patient form requires 14 fields and converts at 2%.",
      "Insurance acceptance buried — local searchers can't tell if you take their plan.",
    ],
    whatWeDo: [
      {
        title: "Procedure-specific SEO",
        body: "Dedicated ranking pages for implants, Invisalign, veneers, crowns, root canals, and emergency dentistry — each tuned to that procedure's buyer intent.",
      },
      {
        title: "Insurance + financing clarity",
        body: "Accepted insurance carriers shown above the fold, plus financing partners (CareCredit, LendingClub) surfaced where high-ticket procedure buyers actually look.",
      },
      {
        title: "Online booking + intake",
        body: "Integration with NexHealth, LocalMed, or your PMS booking widget, plus short-form intake that converts at 8%+ instead of 2%.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver dental is dominated by DSOs (Aspen, Comfort) with massive Google Ads spend. We help private practices win organic + Map Pack on procedure-specific searches DSOs underweight.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military and family dental flow (Tricare-aware, kid-friendly). We build dedicated Tricare-acceptance pages and pediatric content most Springs practices skip.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland skew higher-end cosmetic (veneers, smile design, full-mouth reconstruction). We tune content and photography for that buyer.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Douglas County dental buyers are price-tolerant for quality. Implants and full-arch (All-on-4) cases close at premium price points when site polish matches.",
      },
    ],
    faqs: [
      {
        q: "Will you build separate pages for implants, Invisalign, and veneers?",
        a: "Yes — that's the entire point. Single-procedure landing pages convert 4-6× better than a generic 'cosmetic dentistry' page and rank for higher-intent searches.",
      },
      {
        q: "Can you handle HIPAA-aware contact forms?",
        a: "Yes. We use HIPAA-conduit form vendors with BAAs in place, plus encrypt form submissions to your inbox. Your IT can audit the full setup.",
      },
      {
        q: "Do you integrate with our PMS (Dentrix, Eaglesoft, Open Dental)?",
        a: "Yes — typically through NexHealth, LocalMed, or a custom Zapier route. New-patient leads land in your PMS the same way walk-in patients do.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "orthodontists",
    name: "Orthodontists",
    shortName: "orthodontic practice",
    category: "Health & Wellness",
    metaTitle: "Orthodontist SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Colorado orthodontic marketing for braces, Invisalign, and clear aligner practices. Family-friendly websites and SEO that book consults consistently.",
    heroEyebrow: "Colorado Orthodontic Marketing",
    heroGoldWord: "orthodontic practice",
    heroTitle: "Consult bookings that turn into started cases for your Colorado orthodontic practice",
    heroDescription:
      "We help Colorado orthodontists win against SmileDirect, Candid, and the corporate Invisalign mills — with content, SEO, and sites that earn the in-person consult.",
    pains: [
      "DTC aligner brands eating into the under-$3K case flow.",
      "No clear differentiation between in-house Invisalign vs. SmileDirect alternatives.",
      "Adult Invisalign buyers going to general dentists who advertise harder.",
      "Site reads kids-and-teens only — adult buyers can't see themselves in it.",
    ],
    whatWeDo: [
      {
        title: "Adult vs. teen funnels",
        body: "Separate landing pages and content for adult Invisalign vs. teen braces — different photography, different proof, different conversion path.",
      },
      {
        title: "Anti-DTC positioning",
        body: "Educational content on why in-person ortho beats DTC aligners (case complexity, monitoring, refinements) — without sounding defensive.",
      },
      {
        title: "Free-consult conversion design",
        body: "Free-consult booking widgets, before/after galleries, and financing transparency that turn site visitors into scheduled consults.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver ortho is heavy adult Invisalign in Cherry Creek, Wash Park, and Highlands. We tune content for the 30-50 professional buyer who wants discreet treatment.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy family + teen ortho flow plus military families (PCS-aware, Tricare-aware). We build content for both buyer types in parallel.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder and NoCo have strong adult Invisalign demand and a more research-heavy buyer. We build deep before/after content and case-complexity transparency.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Douglas County family ortho is price-tolerant for premium care. Sites that read polished and surface real case results close higher-ticket cases reliably.",
      },
    ],
    faqs: [
      {
        q: "Will you build content explaining why we beat SmileDirect / Candid?",
        a: "Yes — but framed educationally, not defensively. Buyers want to understand the difference; they don't want to be talked down to.",
      },
      {
        q: "Can you handle our free-consult booking?",
        a: "Yes. We integrate booking widgets (NexHealth, LocalMed, Calendly) directly into the Invisalign and braces landing pages.",
      },
      {
        q: "Will you build separate content for clear aligners vs. traditional braces?",
        a: "Yes. They're different buyers with different intent. Each gets its own ranking page, photography, and conversion path.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "physical-therapy",
    name: "Physical Therapy Clinics",
    shortName: "physical therapy clinic",
    category: "Health & Wellness",
    metaTitle: "Physical Therapy SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado physical therapy clinics — sports PT, post-surgical, dry needling, and pelvic floor. SEO and websites tuned for direct-access referrals.",
    heroEyebrow: "Colorado PT Marketing",
    heroGoldWord: "physical therapy clinic",
    heroTitle: "Direct-access patients that fill the schedule for your Colorado physical therapy clinic",
    heroDescription:
      "Colorado is a direct-access state — patients can come straight to PT without an MD referral. We build SEO and websites that capture that direct-access search demand.",
    pains: [
      "Site doesn't make 'no referral needed' obvious to direct-access searchers.",
      "Sports PT, post-surgical, pelvic floor, and dry needling all share one generic 'services' page.",
      "Cash-pay buyers can't tell if you take their HSA / FSA or what visits cost.",
      "Hospital-system PT clinics outranking you on every neighborhood search.",
    ],
    whatWeDo: [
      {
        title: "Direct-access positioning",
        body: "Above-the-fold messaging that 'Colorado direct access' means no referral needed — converting researchers who didn't know they could skip the MD visit.",
      },
      {
        title: "Specialty-specific pages",
        body: "Dedicated ranking pages for sports PT, post-surgical (ACL, rotator cuff, total knee), dry needling, pelvic floor, vestibular, and runner's gait analysis.",
      },
      {
        title: "Cash-pay + insurance clarity",
        body: "HSA/FSA-eligible messaging, transparent cash-pay rates, and accepted insurance carriers surfaced where new patients actually look.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver PT is dominated by hospital-system clinics (UCHealth, HCA). We help private practices win on neighborhood SEO and specialty pages (running gait, climbing-specific, post-op) the systems underweight.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military PT (Tricare-friendly, post-deployment) plus strong endurance-athlete demand (running, cycling, triathlon). We tune content to both.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland are some of the most active populations in the country. Specialty content for trail running, climbing, cycling, and Nordic skiing converts at premium rates.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Douglas County PT skews family + post-surgical (joint replacements, sports injuries). We build content for both adult athletes and active retirees in parallel.",
      },
    ],
    faqs: [
      {
        q: "Will you build separate content for sports PT vs. pelvic floor vs. post-surgical?",
        a: "Yes. Each specialty has totally different buyers, search intent, and conversion paths. Bundling them into one 'services' page leaves rankings and patients on the table.",
      },
      {
        q: "Can you handle direct-access education content?",
        a: "Yes. Most Coloradans don't know they can skip the MD referral. Educational content explaining direct access converts researchers who otherwise would never have called you.",
      },
      {
        q: "Do you integrate with WebPT or our EMR?",
        a: "Yes — usually through Zapier or a direct API connection. New-patient leads can land directly in WebPT or trigger an SMS to your front desk.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "med-spas",
    name: "Med Spas",
    shortName: "med spa",
    category: "Health & Wellness",
    metaTitle: "Med Spa SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Colorado med spa marketing for Botox, filler, laser, and body contouring. SEO and websites tuned for high-LTV cosmetic buyers and recurring treatment cycles.",
    heroEyebrow: "Colorado Med Spa Marketing",
    heroGoldWord: "med spa",
    heroTitle: "High-LTV cosmetic clients on a recurring schedule for your Colorado med spa",
    heroDescription:
      "Med spa wins are built on recurring treatments — Botox every 3 months, filler every 6, laser packages, body contouring series. We build SEO and sites tuned for that LTV math.",
    pains: [
      "Site looks like a beauty blog instead of a medical practice.",
      "Botox, filler, laser, CoolSculpting, and Morpheus8 all share one 'services' page.",
      "No clear pricing or package structure — buyers leave to compare with practices that publish.",
      "Reviews on RealSelf and Google not connected to the right service categories.",
    ],
    whatWeDo: [
      {
        title: "Treatment-specific landing pages",
        body: "Dedicated ranking pages for Botox, Dysport, lip filler, cheek filler, laser hair removal, IPL, Morpheus8, CoolSculpting, EmSculpt, and skin tightening — each with its own conversion path.",
      },
      {
        title: "Pricing + package transparency",
        body: "Visible package pricing (or honest ranges), membership program details, and financing partners surfaced where high-ticket buyers actually look.",
      },
      {
        title: "Before/after gallery + provider trust",
        body: "Schema-marked before/after galleries (compliant with FDA + Colorado Medical Board), provider credentials, and injector specialty surfaced up front.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver med spa is heavy in Cherry Creek, RiNo, Highlands, and DTC. Buyers compare 3-4 spas before booking — gallery depth and pricing transparency win the comparison.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has growing med spa demand plus military spouse buyers (price-conscious, package-friendly). We tune content for membership programs and recurring treatment economics.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland skew natural-aesthetic and wellness-integrated (PRP, regenerative, IV therapy). We build content for that wellness-leaning buyer.",
      },
      {
        city: "Castle Rock & Centennial",
        body: "Douglas County and Centennial med spa buyers are price-tolerant and recurring-treatment focused. Membership program landing pages and full-treatment ladder content close consistently.",
      },
    ],
    faqs: [
      {
        q: "Will you build out before/after galleries with proper consent + schema?",
        a: "Yes. We use ImageObject schema, document patient consent, and follow Colorado Medical Board guidance on cosmetic before/after marketing.",
      },
      {
        q: "Can you set up our membership program landing page?",
        a: "Yes. Membership programs are some of the highest-LTV med spa work — we build dedicated landing pages, comparison tables, and signup flows for them.",
      },
      {
        q: "Do you handle paid social (Instagram, TikTok) too?",
        a: "We focus on SEO + Google Ads + site conversion. We refer paid social to specialist partners who only do beauty/aesthetic — but we'll integrate the funnels.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "plastic-surgeons",
    name: "Plastic Surgeons",
    shortName: "plastic surgery practice",
    category: "Health & Wellness",
    metaTitle: "Plastic Surgeon SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado plastic surgeons — breast aug, mommy makeover, rhino, body contouring. Trust-building websites and SEO for premium cosmetic buyers.",
    heroEyebrow: "Colorado Plastic Surgery Marketing",
    heroGoldWord: "plastic surgery practice",
    heroTitle: "Qualified consultations for your Colorado plastic surgery practice",
    heroDescription:
      "Plastic surgery buyers research for months. We build SEO and websites that earn their trust during that research window — so when they book a consult, they're ready to commit.",
    pains: [
      "Site looks generic against board-certified competitors with deeper galleries.",
      "Procedure pages too thin — buyers compare to practices with full pre/post/recovery education.",
      "RealSelf reviews disconnected from your site's authority.",
      "Mommy makeover, BBL, and breast aug buyers going to practices with stronger social proof.",
    ],
    whatWeDo: [
      {
        title: "Procedure-deep content",
        body: "Pre-op education, recovery timelines, risk transparency, and surgical approach detail for every procedure you perform — written to the standard premium buyers expect.",
      },
      {
        title: "Board-certified credibility stack",
        body: "ABPS certification, hospital privileges, fellowship training, and society memberships (ASAPS, ASPS) surfaced where serious buyers actually look.",
      },
      {
        title: "Before/after galleries + consent",
        body: "Properly consented, ImageObject-marked before/after galleries that win the gallery-depth comparison without crossing FDA / Medical Board lines.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver plastic surgery is dominated by 6-8 high-volume board-certified practices. We help boutique practices win on procedure depth and personal-attention positioning the high-volume groups can't match.",
      },
      {
        city: "Colorado Springs",
        body: "Springs plastic surgery is growing fast — heavy mommy makeover, breast aug, and post-massive-weight-loss demand. We tune content for both military families and civilian buyers.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland skew natural-result and minimally-invasive (fat transfer, deep plane facelift, regenerative). We tune content for that aesthetic-philosophy buyer.",
      },
      {
        city: "Castle Rock & Centennial",
        body: "Douglas County and Centennial plastic surgery buyers are research-heavy and brand-conscious. Sites that read premium, board-certified, and personal close higher-ticket cases.",
      },
    ],
    faqs: [
      {
        q: "Will you respect Colorado Medical Board + FDA cosmetic marketing rules?",
        a: "Yes. We follow Colorado Medical Board guidance on board certification claims, before/after consent, and FDA rules on off-label drug + device marketing.",
      },
      {
        q: "Can you build out full procedure-education content?",
        a: "Yes — pre-op, recovery, risks, alternatives, and post-op care for every procedure. Premium buyers expect this depth and your site won't compete without it.",
      },
      {
        q: "Will you handle consultation booking integration?",
        a: "Yes. Most practices we work with integrate Calendly, NexHealth, or a custom intake form wired to their EHR / practice management.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "dermatologists",
    name: "Dermatologists",
    shortName: "dermatology practice",
    category: "Health & Wellness",
    metaTitle: "Dermatologist SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado dermatology practices — medical, surgical, and cosmetic. SEO and websites for skin cancer, acne, eczema, and aesthetic procedures.",
    heroEyebrow: "Colorado Dermatology Marketing",
    heroGoldWord: "dermatology practice",
    heroTitle: "Patients across medical, surgical, and cosmetic for your Colorado dermatology practice",
    heroDescription:
      "Dermatology buyers come from three completely different buckets — medical (acne, eczema, skin cancer), surgical (Mohs), and cosmetic (Botox, lasers). We build SEO and sites for all three.",
    pains: [
      "Medical, surgical, and cosmetic content all stuck on one generic 'services' page.",
      "Skin cancer and Mohs buyers can't tell if you're fellowship-trained.",
      "Cosmetic competitors (med spas) outranking you on Botox and laser searches.",
      "New-patient appointment lead time pushing patients to competitors with online booking.",
    ],
    whatWeDo: [
      {
        title: "Three-funnel content architecture",
        body: "Separate ranking pages for medical (acne, eczema, psoriasis), surgical (Mohs, excisions, biopsies), and cosmetic (Botox, filler, lasers, chemical peels) — each tuned to that buyer.",
      },
      {
        title: "Sub-specialty credibility",
        body: "Mohs fellowship, pediatric derm, cosmetic derm, and dermatopathology credentials surfaced where serious buyers compare.",
      },
      {
        title: "Online booking + insurance clarity",
        body: "Real-time booking integration, accepted insurance plans surfaced clearly, and intake forms tuned to the specific concern (skin check vs. Botox vs. acne consult).",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver derm is heavily competitive across all three buckets. We help practices win on sub-specialty depth (Mohs, pediatric, complex medical) where general practices and med spas can't compete.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has high skin cancer rates from elevation + sun exposure. Skin cancer screening and Mohs surgery content converts strongly when surfaced properly.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Fort Collins, and Loveland skew preventive + cosmetic (annual skin checks, sun damage, anti-aging). We tune content for that wellness-integrated buyer.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Douglas County derm sees heavy family + cosmetic flow. We build content for both pediatric medical (eczema, acne) and adult cosmetic in parallel.",
      },
    ],
    faqs: [
      {
        q: "Will you separate medical, surgical, and cosmetic content?",
        a: "Yes — they're three different buyers with three different intents. Bundling them costs rankings and conversions on all three.",
      },
      {
        q: "Can you handle Mohs-specific content and credentialing?",
        a: "Yes. Mohs fellowship credentials and ACMS membership are major trust signals for skin cancer patients — we surface them properly.",
      },
      {
        q: "Do you integrate with EMA, ModMed, or other derm-specific EMRs?",
        a: "Yes — typically through online booking partners (NexHealth, LocalMed) or direct API integration where available.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "ophthalmologists",
    name: "Ophthalmologists",
    shortName: "ophthalmology practice",
    category: "Health & Wellness",
    metaTitle: "Ophthalmologist SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado ophthalmology practices — LASIK, cataract, refractive lens exchange, and medical eye care. SEO and websites built for premium surgical procedures.",
    heroEyebrow: "Colorado Eye Care Marketing",
    heroGoldWord: "ophthalmology practice",
    heroTitle: "LASIK, cataract, and medical patients for your Colorado ophthalmology practice",
    heroDescription:
      "LASIK and refractive lens exchange are research-heavy, premium-ticket decisions. We build SEO and sites that win the buyer comparison across all three Colorado regions.",
    pains: [
      "LASIK content reads identical to LasikPlus, TLC, and the chain competitors.",
      "Cataract surgery patients can't tell if you offer premium IOLs (toric, multifocal, EDOF) vs. only standard.",
      "No clear pricing on LASIK or RLE — buyers leave to compare with practices that publish.",
      "Medical eye care (glaucoma, retina, dry eye) hidden behind surgical content.",
    ],
    whatWeDo: [
      {
        title: "LASIK + refractive depth",
        body: "Dedicated content for LASIK, PRK, SMILE, ICL, and RLE — with technology, candidacy, recovery, and pricing transparency that wins the buyer comparison.",
      },
      {
        title: "Premium IOL education",
        body: "Cataract surgery content that surfaces premium IOL options (toric, multifocal, EDOF, light-adjustable) where the upgrade decision actually happens.",
      },
      {
        title: "Medical + surgical separation",
        body: "Glaucoma, retina, dry eye, and other medical eye care gets its own ranking pages — separate buyers, separate intent, separate conversion paths.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver LASIK is dominated by LasikPlus, TLC, and 2-3 high-volume independents. We help boutique practices win on technology depth (SMILE, ICL, premium IOL) and surgeon-specific credibility.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military LASIK + refractive demand (active-duty waiver process, post-deployment). Specialized military-friendly content converts strongly.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder and NoCo skew premium IOL and refractive lens exchange (educated, research-heavy buyers). Pricing transparency and technology depth win consistently.",
      },
      {
        city: "Castle Rock & Centennial",
        body: "Douglas County and Centennial buyers are price-tolerant and research-heavy for premium surgical care. Site polish and surgeon credentials close higher-ticket cases.",
      },
    ],
    faqs: [
      {
        q: "Will you publish LASIK pricing or keep it gated?",
        a: "We strongly recommend transparent pricing (or honest ranges). Buyers comparing LASIK leave gated-pricing sites at 4× the rate of transparent ones.",
      },
      {
        q: "Can you build content for premium IOL upgrades?",
        a: "Yes — premium IOL education is some of the highest-ROI cataract content you can build. Most cataract patients don't know multifocal or EDOF exists until you show them.",
      },
      {
        q: "Do you handle medical eye care content too (glaucoma, retina, dry eye)?",
        a: "Yes. Each gets its own ranking page tuned to that condition's buyer intent — different than the surgical funnels.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "commercial-roofing",
    name: "Commercial Roofing",
    shortName: "commercial roofing contractor",
    category: "Commercial & Industrial",
    metaTitle: "Commercial Roofing SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado commercial roofing contractors — TPO, EPDM, modified bitumen, metal, and roof restoration. SEO for property managers, GCs, and facility directors.",
    heroEyebrow: "Colorado Commercial Roofing Marketing",
    heroGoldWord: "commercial roofing contractor",
    heroTitle: "Property managers and GCs that send real bid invites to your Colorado commercial roofing contractor business",
    heroDescription:
      "Commercial roofing has a totally different buyer than residential — property managers, facility directors, and GCs who need bonding, references, and TPO/EPDM expertise. We build SEO and sites for that buyer.",
    pains: [
      "Site reads residential — property managers don't see themselves in it.",
      "No content separating TPO, EPDM, modified bitumen, metal, or coatings.",
      "Bonding capacity, certifications (NRCA, manufacturer), and references not surfaced.",
      "Storm-restoration insurance work being claimed by residential roofers chasing volume.",
    ],
    whatWeDo: [
      {
        title: "B2B-buyer site architecture",
        body: "Dedicated property manager + GC funnels with bonding capacity, references, certifications, and bid-invite forms — not residential CTAs.",
      },
      {
        title: "System-specific content",
        body: "Separate ranking pages for TPO, EPDM, modified bitumen, standing seam metal, coatings, and tear-off/replacement — each tuned to the spec buyer.",
      },
      {
        title: "Maintenance program landing",
        body: "Recurring maintenance contracts are the highest-LTV commercial roofing work. We build dedicated landing pages, RFP-ready proposals, and inspection report download flows.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver commercial roofing is competitive across the I-70 corridor, DTC, and Denver Tech Center. Bonding capacity and maintenance program depth win the property-manager comparison.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military, federal, and school-district commercial roofing flow plus growing flex/industrial. We tune content for federal certifications and prevailing wage compliance.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo, Cañon City, and Trinidad have strong industrial + agricultural commercial roofing demand (steel, metal, modified bitumen). Less crowded than Front Range — focused content moves rankings.",
      },
      {
        city: "NoCo + Northern Colorado",
        body: "Fort Collins, Loveland, and Greeley have heavy ag + light industrial commercial roofing. We build content tuned to those specific facility types.",
      },
    ],
    faqs: [
      {
        q: "Will you keep residential out of the messaging?",
        a: "Yes — that's the entire point. Property managers and GCs need to see themselves immediately. Residential CTAs and imagery actively kill commercial conversion.",
      },
      {
        q: "Can you build out RFP and bid-invite intake?",
        a: "Yes. Most property managers and GCs need a quick bid form (project size, system type, timeline, contact). We build it short, scannable, and conversion-tested.",
      },
      {
        q: "Do you build content for federal + prevailing wage projects?",
        a: "Yes. Federal certifications, Davis-Bacon wage compliance, and bonding capacity are real differentiators for Springs especially — we surface them properly.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "steel-buildings",
    name: "Steel Building Companies",
    shortName: "steel building company",
    category: "Commercial & Industrial",
    metaTitle: "Steel Building Company SEO & Web Design | Colorado",
    metaDescription:
      "Marketing for Colorado steel building manufacturers and erectors — pre-engineered metal buildings, ag, industrial, commercial, and storage. Built for spec buyers.",
    heroEyebrow: "Colorado Steel Buildings Marketing",
    heroGoldWord: "steel building company",
    heroTitle: "Spec-ready buyers for your Colorado steel building company",
    heroDescription:
      "Steel building buyers are spec-driven — they want clear-span widths, snow loads, permit help, and erection capacity. We build SEO and sites that speak to that buyer instead of generic 'metal building' fluff.",
    pains: [
      "Site competes with national brands (Mueller, Worldwide Steel) on price instead of local install + permit help.",
      "No content separating ag, industrial, commercial, residential garage, or storage buildings.",
      "Snow load, wind load, and Colorado-specific code requirements not surfaced.",
      "Permit and erection help (the local advantage) buried below national-brand competitor specs.",
    ],
    whatWeDo: [
      {
        title: "Application-specific landing pages",
        body: "Dedicated content for ag/equestrian, commercial/industrial, mini-storage, residential garage, aircraft hangar, and shop buildings — each tuned to that buyer.",
      },
      {
        title: "Colorado-code spec content",
        body: "Snow load (Front Range vs. mountains), wind load, seismic, and county-specific permitting content that national brands can't write.",
      },
      {
        title: "Local-erection advantage",
        body: "Site messaging that surfaces the things national kit sellers can't offer — local erection crews, permit help, foundation, and turnkey delivery.",
      },
    ],
    citySnippets: [
      {
        city: "Front Range (Denver / Springs)",
        body: "Front Range steel buildings skew commercial, mini-storage, and residential garage/shop. Snow load and county permit help win against national kit sellers shipping in from Texas.",
      },
      {
        city: "Eastern Plains + Pueblo",
        body: "Eastern Colorado and Pueblo have heavy ag + equestrian demand (barns, hay storage, equipment buildings). We tune content to those specific applications.",
      },
      {
        city: "Western Slope + Mountain Towns",
        body: "Western Slope and mountain communities (Aspen, Telluride, Steamboat) need steel buildings rated for extreme snow loads + altitude wind. Specialized engineering content converts strongly.",
      },
      {
        city: "NoCo + Northern Colorado",
        body: "Fort Collins, Loveland, Greeley have strong ag + light industrial demand. Permit help across Larimer, Weld, and Boulder counties is a major differentiator.",
      },
    ],
    faqs: [
      {
        q: "Will you build out content for ag vs. industrial vs. residential garage buyers?",
        a: "Yes. Each is a totally different buyer with different intent, different sizing, and different decision process. Bundling them into 'steel buildings' costs rankings on all three.",
      },
      {
        q: "Can you create snow load + wind load reference content?",
        a: "Yes. County-by-county snow/wind load reference content is exactly what serious buyers research before they commit. National kit sellers don't bother — easy long-tail wins.",
      },
      {
        q: "Do you handle 3D building configurator integration?",
        a: "Yes — if you have a configurator (or want one), we wire it into the site as a primary lead-capture path with full attribution tracking.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "custom-home-builders",
    name: "Custom Home Builders",
    shortName: "custom home builder",
    category: "Commercial & Industrial",
    metaTitle: "Custom Home Builder SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado custom home builders — luxury, mountain, and semi-custom. SEO and websites tuned for high-net-worth buyers and 18-month sales cycles.",
    heroEyebrow: "Colorado Custom Home Builder Marketing",
    heroGoldWord: "custom home builder",
    heroTitle: "Qualified luxury buyers for your Colorado custom home builder",
    heroDescription:
      "Custom home buyers research for 6-18 months before they commit. We build sites and SEO that earn their trust during that long research window — and convert when they're ready.",
    pains: [
      "Portfolio photography looks production-builder generic instead of luxury custom.",
      "No content explaining the build process, timeline, or what a $1.5M+ custom home actually involves.",
      "Mountain-builder differentiation (snow loads, propane, septic, well, off-grid) buried.",
      "High-net-worth buyers leaving to builders with deeper portfolios and process transparency.",
    ],
    whatWeDo: [
      {
        title: "Portfolio-depth design",
        body: "Photography-first home pages with deep project case studies — not just gallery dumps. Each completed home tells a story with budget range, timeline, and architectural intent.",
      },
      {
        title: "Build-process transparency",
        body: "Long-form content explaining design phase, permitting, foundation, framing, finish, and warranty — the things HNW buyers compare across 3-5 builders before they commit.",
      },
      {
        title: "Mountain + custom-spec niches",
        body: "Specialized content for mountain construction, ICF, passive house, net-zero, and off-grid that production builders can't write.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro + Foothills",
        body: "Denver metro custom homes ($1-3M+) win on portfolio polish and process transparency. Cherry Hills, Cherry Creek, Wash Park, and the foothills (Evergreen, Conifer, Morrison) compare 4-5 builders before committing.",
      },
      {
        city: "Colorado Springs + Black Forest",
        body: "Springs custom homes are heavy in Black Forest, Broadmoor, and the unincorporated foothills. Mountain-build expertise (well, septic, propane, road access) is a major differentiator.",
      },
      {
        city: "Mountain Towns",
        body: "Aspen, Vail, Telluride, Steamboat, Crested Butte custom homes hit $5M+. Buyers expect production-grade portfolio sites, full team bios, and architect-relationship transparency.",
      },
      {
        city: "Castle Rock & Castle Pines",
        body: "Castle Rock and Castle Pines custom homes ($1-4M) skew high-end semi-custom on developer lots plus full-custom on Castle Pines Village land. Site polish matches the buyer's expectations.",
      },
    ],
    faqs: [
      {
        q: "Will you build out individual project case studies vs. just a gallery?",
        a: "Yes. Project-depth case studies (with budget range, timeline, owner narrative, photography) are what HNW buyers actually evaluate. Galleries alone don't close.",
      },
      {
        q: "Can you handle architect-collaboration content?",
        a: "Yes. Most custom home buyers have a relationship with an architect first. We build content that positions you as the builder architects want to work with.",
      },
      {
        q: "Do you handle pre-construction lead nurture?",
        a: "Yes. Custom home sales cycles are 6-18 months. We build email + content nurture flows that keep prospects engaged through that whole window.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "trucking-hauling",
    name: "Trucking & Hauling Companies",
    shortName: "trucking and hauling company",
    category: "Commercial & Industrial",
    metaTitle: "Trucking & Hauling SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado trucking, hauling, and freight companies — dump trucks, flatbed, dry van, refrigerated, dirt/aggregate. B2B SEO for shippers and GCs.",
    heroEyebrow: "Colorado Trucking & Hauling Marketing",
    heroGoldWord: "trucking and hauling company",
    heroTitle: "Direct shipper and GC contracts for your Colorado trucking and hauling company",
    heroDescription:
      "Direct shipper and GC contracts pay 2-3× what broker loads pay. We build SEO and websites that win those direct relationships — instead of leaving you stuck on load boards.",
    pains: [
      "Stuck on DAT, Truckstop, and broker boards earning broker margins instead of direct.",
      "Site (if any) doesn't surface DOT/MC numbers, fleet size, or insurance.",
      "Dump truck, flatbed, refrigerated, hot shot, and HHG all share one 'services' page.",
      "Local GCs and shippers don't know you exist because Google doesn't show you.",
    ],
    whatWeDo: [
      {
        title: "Direct-shipper site",
        body: "Carrier credentials (DOT/MC, insurance, equipment list), service-area maps, and shipper-friendly RFQ forms that convert direct relationships instead of broker calls.",
      },
      {
        title: "Equipment + lane-specific pages",
        body: "Separate ranking pages for dump truck, flatbed, dry van, reefer, hot shot, HHG, dirt/aggregate, and dedicated routes — each tuned to that shipper's intent.",
      },
      {
        title: "Local SEO for hauling work",
        body: "Construction hauling, dirt + aggregate, demolition debris, snow removal — local search work that GCs and developers actually search for in your market.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver hauling is heavy construction, demolition, and aggregate work driven by the build boom. Local SEO for dump truck and dirt hauling captures GC searches that broker boards miss.",
      },
      {
        city: "Colorado Springs",
        body: "Springs has heavy military hauling (Fort Carson, Peterson, Schriever), federal contracts, plus growing residential construction. We tune content to both federal and commercial buyers.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo, Cañon City, and Trinidad have heavy industrial, ag, and energy hauling demand. Less competition online means a focused SEO push captures direct shipper relationships fast.",
      },
      {
        city: "Eastern Plains + NoCo",
        body: "Eastern Colorado and NoCo have strong ag hauling (grain, livestock, hay) plus oil + gas service hauling. We build content tuned to those specific commodity flows.",
      },
    ],
    faqs: [
      {
        q: "Can you help us reduce dependence on load boards?",
        a: "Yes — that's usually the explicit goal. Direct shipper relationships pay 2-3× broker loads. We build the organic + Map Pack channels you need to win those relationships.",
      },
      {
        q: "Will you build separate content for dump truck vs. dry van vs. reefer?",
        a: "Yes. Each equipment type has totally different shippers and search intent. One generic 'trucking services' page leaves rankings and contracts on the table.",
      },
      {
        q: "Do you handle SAFER/FMCSA reference integration?",
        a: "Yes. We surface DOT/MC numbers, FMCSA safety ratings, and insurance certificates where serious shippers actually look before they call.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "equipment-rental",
    name: "Equipment Rental Companies",
    shortName: "equipment rental company",
    category: "Commercial & Industrial",
    metaTitle: "Equipment Rental SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado equipment rental companies — construction, party, tool, and heavy equipment. SEO and websites that win against United Rentals and Sunbelt.",
    heroEyebrow: "Colorado Equipment Rental Marketing",
    heroGoldWord: "equipment rental company",
    heroTitle: "Local rental customers your Colorado equipment rental company actually wants",
    heroDescription:
      "Independent rental companies win on local relationships, fleet availability, and same-day pickup — not on national-chain brand recognition. We build SEO and sites that surface those advantages.",
    pains: [
      "Outranked by United Rentals, Sunbelt, and Home Depot Rental on every category search.",
      "No real-time fleet availability surfaced — buyers leave to call the chain.",
      "Construction equipment, tool, party, and DIY all bundled into one generic catalog.",
      "Local contractor relationships not amplified online — repeat customers, but no new ones.",
    ],
    whatWeDo: [
      {
        title: "Category-deep landing pages",
        body: "Dedicated ranking pages for skid steer, mini ex, scissor lift, boom lift, generator, compressor, dumpster, and tool rental — each with availability and rate transparency.",
      },
      {
        title: "Local-first positioning",
        body: "Same-day pickup, real local contact, fleet availability, and contractor-friendly billing surfaced where the chains can't compete on personal relationship.",
      },
      {
        title: "Online quote + reservation",
        body: "Quote-request flows tuned to the rental decision (equipment, dates, jobsite location, delivery vs. pickup) — not generic contact forms.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver rental is dominated by United and Sunbelt. We help independents win on contractor relationships, neighborhood SEO, and the equipment categories the chains underweight.",
      },
      {
        city: "Colorado Springs",
        body: "Springs rental has heavy military + federal contract demand plus growing residential build. We tune content to both commercial-fleet and homeowner DIY buyers.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo, Cañon City, and southern Colorado have lighter chain competition and strong ag + industrial rental demand. Local SEO + Map Pack work moves rankings fast.",
      },
      {
        city: "Mountain Towns",
        body: "Aspen, Vail, Steamboat, Crested Butte have heavy seasonal construction + property management rental demand. Mountain-specific equipment and delivery logistics are real differentiators.",
      },
    ],
    faqs: [
      {
        q: "Can you help us beat United Rentals on local searches?",
        a: "Yes. United and Sunbelt win on brand but lose on local relationship and same-day availability. We build SEO that surfaces those advantages where buyers actually compare.",
      },
      {
        q: "Will you integrate our rental management software?",
        a: "Yes. Most rental software (Point of Rental, Wynne, Texada) has APIs or Zapier integration we can use to surface real-time availability and bookings.",
      },
      {
        q: "Do you build content for both contractor and DIY buyers?",
        a: "Yes. Each is a different buyer with different intent and price sensitivity. Bundling them into one catalog leaves both rankings and rentals on the table.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "manufacturing",
    name: "Manufacturing Companies",
    shortName: "manufacturer",
    category: "Commercial & Industrial",
    metaTitle: "Manufacturing SEO & Web Design in Colorado | CWI",
    metaDescription:
      "B2B marketing for Colorado manufacturers — CNC machining, metal fab, contract manufacturing, and OEM. SEO and websites tuned for procurement buyers and engineers.",
    heroEyebrow: "Colorado Manufacturing Marketing",
    heroGoldWord: "manufacturer",
    heroTitle: "Procurement buyers and engineers find your Colorado manufacturer",
    heroDescription:
      "Manufacturing buyers (engineers, procurement, sourcing) search differently than consumers — they want capabilities, certifications, lead times, and quality systems surfaced fast. We build SEO and sites for that buyer.",
    pains: [
      "Site reads like a brochure — no capabilities matrix, no equipment list, no certifications surfaced.",
      "Engineers can't find your tolerances, material capabilities, or finishing options.",
      "AS9100, ISO 9001, ITAR, and other certifications buried instead of front-and-center.",
      "RFQ form too long — procurement abandons it for competitors with shorter forms.",
    ],
    whatWeDo: [
      {
        title: "Capabilities-first site architecture",
        body: "Equipment list, material capabilities, tolerance ranges, finishing options, and quality certifications surfaced where engineers and buyers actually look.",
      },
      {
        title: "Industry-vertical landing pages",
        body: "Dedicated pages for the verticals you serve — aerospace, defense, medical, semiconductor, energy, ag — each tuned to that industry's qualification requirements.",
      },
      {
        title: "RFQ + ITAR-aware intake",
        body: "Short-form RFQ flows, file-upload for prints (with proper security for ITAR-controlled work), and routing that gets the right engineer responding within hours.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver manufacturing is heavy aerospace (Lockheed, Ball, Sierra Space), medical device, and contract CNC. We build content for AS9100, FDA-registered, and ITAR work that wins qualified RFQs.",
      },
      {
        city: "Colorado Springs",
        body: "Springs is one of the densest defense + space manufacturing clusters in the country. ITAR, CMMC, NIST 800-171 certifications and government contract experience are major search drivers.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder, Longmont, Loveland, and Fort Collins are heavy in semiconductor, life sciences, and clean tech manufacturing. We tune content to those specific buyer technical requirements.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo has strong steel, rail, and energy-sector manufacturing demand. Less crowded SERPs than Front Range — focused content captures qualified inbound RFQs fast.",
      },
    ],
    faqs: [
      {
        q: "Can you handle ITAR-aware site security?",
        a: "Yes. We build file-upload flows that respect ITAR-controlled file storage requirements, plus surface your CMMC, NIST 800-171, or DFARS posture appropriately.",
      },
      {
        q: "Will you build content for our specific industry verticals?",
        a: "Yes. Aerospace, medical, semiconductor, defense — each has its own qualification language and search intent. We build dedicated pages for each vertical you serve.",
      },
      {
        q: "Do you integrate with ERP / quoting systems (JobBOSS, ProShop, etc.)?",
        a: "Yes — typically through API or Zapier. RFQs land directly in your quoting system instead of bouncing through email.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "franchise-businesses",
    name: "Franchise Businesses",
    shortName: "franchise location",
    category: "Professional Services",
    metaTitle: "Franchise Business SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Local marketing for Colorado franchise locations — brand-compliant websites and SEO that beat corporate-built sites. Multi-location SEO done right.",
    heroEyebrow: "Colorado Franchise Marketing",
    heroGoldWord: "franchise location",
    heroTitle: "Local search wins for your Colorado franchise location — without breaking brand standards",
    heroDescription:
      "Most franchise corporate sites are built for the brand, not for local search. We build supplemental local sites and SEO that win Map Pack and city-specific searches without violating brand standards.",
    pains: [
      "Corporate-built location pages don't rank locally and you can't edit them.",
      "Other franchisees in nearby Colorado cities outranking your location.",
      "Brand standards limit how much you can customize — but your local market needs more.",
      "Lead-gen from corporate site doesn't attribute properly to your location's revenue.",
    ],
    whatWeDo: [
      {
        title: "Brand-compliant local site",
        body: "Supplemental local site (or franchisee microsite where allowed) that respects brand standards but adds the local SEO and content corporate doesn't build.",
      },
      {
        title: "City + neighborhood SEO",
        body: "Map Pack work, neighborhood pages, and review systems tuned to your local market — without competing with other franchisees in adjacent territories.",
      },
      {
        title: "Local lead attribution",
        body: "Call tracking and form attribution that shows which leads came from local SEO vs. corporate vs. paid — so you can see real local marketing ROI.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver metro franchise locations compete with each other across Aurora, Lakewood, Centennial, Highlands Ranch — neighborhood-specific SEO is the only way to differentiate inside one brand.",
      },
      {
        city: "Colorado Springs",
        body: "Springs franchise locations need to compete against Denver metro brand spend. Local SEO + neighborhood pages (north Springs, Powers, Briargate) win without raising corporate's eyebrows.",
      },
      {
        city: "Pueblo & Southern Colorado",
        body: "Pueblo franchise locations often have the territory to themselves but no local SEO presence. Quick Map Pack wins available with focused effort.",
      },
      {
        city: "NoCo + Mountain Towns",
        body: "Fort Collins, Loveland, mountain town franchise locations need brand-compliant local sites tuned to those specific markets — not Denver-template repeats.",
      },
    ],
    faqs: [
      {
        q: "Will you respect our franchise brand standards?",
        a: "Yes. We work with corporate brand standards documents directly and confirm every design + content decision against those standards before launch.",
      },
      {
        q: "Can you handle multi-location franchisee SEO too?",
        a: "Yes. If you own 3+ locations, we build a coordinated local SEO strategy that grows all of them without internal cannibalization.",
      },
      {
        q: "Will you set up local lead attribution separate from corporate?",
        a: "Yes. Call tracking, UTM attribution, and form routing that show your specific location's marketing ROI — not aggregated corporate numbers.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  {
    slug: "mortgage-brokers",
    name: "Mortgage Brokers",
    shortName: "mortgage brokerage",
    category: "Professional Services",
    metaTitle: "Mortgage Broker SEO & Web Design in Colorado | CWI",
    metaDescription:
      "Marketing for Colorado mortgage brokers and loan officers — purchase, refi, FHA, VA, jumbo, and DSCR. SEO and websites tuned for rate-shoppers and pre-approval intent.",
    heroEyebrow: "Colorado Mortgage Marketing",
    heroGoldWord: "mortgage brokerage",
    heroTitle: "Purchase and refi leads your Colorado mortgage brokerage actually wants",
    heroDescription:
      "Mortgage buyers compare 3-5 lenders before they lock. We build SEO and websites that win that comparison — with rate transparency, loan-product depth, and pre-approval flows that convert.",
    pains: [
      "Outranked by Rocket, Better.com, and big-box bank sites on every search.",
      "No content separating purchase, refi, FHA, VA, jumbo, conventional, or DSCR.",
      "Realtor referral relationships strong, but no local SEO bringing in direct buyers.",
      "Pre-approval form requires 12 fields and converts at 1%.",
    ],
    whatWeDo: [
      {
        title: "Loan-product-specific landing pages",
        body: "Dedicated ranking pages for FHA, VA, jumbo, DSCR, conventional, refi, cash-out refi, and HELOC — each tuned to that buyer's intent and rate sensitivity.",
      },
      {
        title: "Rate + program transparency",
        body: "Honest rate ranges, program eligibility, and loan-officer-specific specialties surfaced where rate-shoppers actually compare.",
      },
      {
        title: "Pre-approval conversion design",
        body: "Short-form pre-approval flows (5-7 fields) that convert at 6-10% instead of 1%. Lead routing to the right LO based on loan type and location.",
      },
    ],
    citySnippets: [
      {
        city: "Denver Metro",
        body: "Denver mortgage is dominated by big-box and online (Rocket, Better, UWM). We help local brokers win on jumbo expertise, condo financing, and the loan complexity online lenders punt on.",
      },
      {
        city: "Colorado Springs",
        body: "Springs is one of the densest VA loan markets in the country (Fort Carson, Peterson, Schriever, Air Force Academy, Cheyenne Mountain). VA-specialist content converts strongly.",
      },
      {
        city: "Boulder & NoCo",
        body: "Boulder and NoCo have heavy jumbo + non-warrantable condo financing demand plus growing DSCR investor flow. Specialist content for these complex products wins.",
      },
      {
        city: "Mountain Towns",
        body: "Aspen, Vail, Steamboat have heavy super-jumbo + second-home financing. Specialized content for non-conforming, asset-based, and bank-statement loans converts at premium values.",
      },
    ],
    faqs: [
      {
        q: "Can you handle TRID + RESPA marketing compliance?",
        a: "Yes. We follow RESPA Section 8 referral rules, TRID rate-quote disclaimers, and NMLS-required disclosures on every page we ship.",
      },
      {
        q: "Will you build content for VA loans specifically?",
        a: "Yes. VA loans are one of the highest-converting Colorado mortgage niches, especially in Springs. Dedicated VA content (entitlement, funding fee, COE, IRRRL) wins consistently.",
      },
      {
        q: "Do you integrate with our LOS (Encompass, Lender Price, etc.)?",
        a: "Yes. We can route pre-approval submissions directly into Encompass or trigger a workflow in your CRM (Surefire, Total Expert) on every new lead.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },

  /* ============================================================
     2026-05 EXPANSION — niche local services with low agency
     competition and high search intent in Colorado markets.
  ============================================================ */
  {
    slug: "dog-trainers",
    name: "Dog Trainers & K9 Schools",
    shortName: "dog training business",
    category: "Health & Wellness",
    metaTitle: "Dog Trainer Marketing & SEO in Colorado | CWI",
    metaDescription:
      "Fill your training calendar with board-and-train, puppy class, and behavior modification clients. Local SEO and websites built for Colorado dog trainers.",
    heroEyebrow: "Colorado Dog Trainer Marketing",
    heroGoldWord: "dog training business",
    heroTitle: "Websites and SEO that fill the calendar for your Colorado dog training business",
    heroDescription:
      "Board-and-train, puppy basics, reactivity rehab, off-leash and protection — we build sites and local SEO that turn nervous dog owners into booked clients.",
    pains: [
      "Petco/Petsmart group classes outranking you for 'puppy training near me'.",
      "Board-and-train slots sit empty for weeks because the website doesn't sell the program.",
      "Aversive vs. positive-reinforcement debate makes prospects bounce before reading.",
      "Behavior modification (reactivity, aggression) is invisible on the site.",
    ],
    whatWeDo: [
      {
        title: "Program-specific service pages",
        body: "Board-and-train, puppy class, private lessons, behavior mod, and protection sport — each program gets its own page so the right clients self-qualify.",
      },
      {
        title: "Trust-first website",
        body: "Trainer credentials (CCPDT, IACP, IGP titles), real before/after video, and pricing transparency placed where parents actually decide.",
      },
      {
        title: "Local SEO + Map Pack",
        body: "Optimized Google Business Profile and city-by-city service pages so you show up first across Colorado Springs, Monument, Falcon, and the metro.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Springs has thousands of military households cycling through every year — many with rescue dogs and PCS-stress behavior issues. Board-and-train demand here is real and chronically underserved.",
      },
      {
        city: "Monument & Tri-Lakes",
        body: "Higher-end Tri-Lakes households want premium private training and off-leash reliability. We position your program for that buyer specifically.",
      },
      {
        city: "Denver Metro",
        body: "Reactive-dog rehab is a massive Denver search category and most trainers compete on the same five generic keywords. We win the long-tail.",
      },
      {
        city: "Falcon & Black Forest",
        body: "Acreage families want gun-dog, livestock-guardian, and recall-focused training. We build content that maps to those specific outcomes.",
      },
    ],
    faqs: [
      {
        q: "Will you help us fill our board-and-train slots specifically?",
        a: "Yes — board-and-train is the highest-value program for most Colorado trainers, so we build dedicated landing pages, FAQs, and Google Ads campaigns around it.",
      },
      {
        q: "Can you market positive-reinforcement and balanced training honestly?",
        a: "Yes. We write copy that's true to your method without picking a fight. Owners who care self-qualify; the wrong-fit clients filter themselves out.",
      },
      {
        q: "Do you handle protection sport, IGP, or working-dog programs?",
        a: "Yes. These are niche, high-ticket programs and we build separate pages targeting working-dog owners specifically — not pet owners.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
  {
    slug: "auto-dealers",
    name: "Independent Auto Dealers",
    shortName: "auto dealership",
    category: "Retail, Food & Auto",
    metaTitle: "Independent Auto Dealer Marketing in Colorado | CWI",
    metaDescription:
      "Move more units for your Colorado independent auto dealership. Inventory-driven SEO, Google Maps, and websites that convert price shoppers into showroom visits.",
    heroEyebrow: "Colorado Auto Dealer Marketing",
    heroGoldWord: "auto dealership",
    heroTitle: "Websites and SEO that move units for your Colorado auto dealership",
    heroDescription:
      "Used cars, trucks, off-road, classic, and BHPH — we build inventory-driven sites and local SEO that turn Cars.com shoppers into your showroom visits.",
    pains: [
      "Cars.com, AutoTrader, and CarGurus capturing all the high-intent traffic.",
      "Inventory feed updates break the website every other week.",
      "Franchise dealers dominate the Map Pack on every used-car search.",
      "BHPH and credit-challenged buyers are a different audience the site doesn't serve.",
    ],
    whatWeDo: [
      {
        title: "Inventory-integrated website",
        body: "Live inventory feed (vAuto, Frazer, DealerCenter, Wayne Reaves) wired into a fast, mobile-first site so every unit gets its own indexable page.",
      },
      {
        title: "Vehicle + segment SEO",
        body: "Ranking pages for trucks, lifted 4x4s, BHPH, classic, AWD, and the specific makes/models you actually carry — not generic 'used cars Colorado' fluff.",
      },
      {
        title: "Map Pack + reputation",
        body: "Optimized Google Business Profile, review velocity from delivered units, and honest dealership content that beats the 'sleazy dealer' Google search bias.",
      },
    ],
    citySnippets: [
      {
        city: "Colorado Springs",
        body: "Springs is a 4x4 and lifted-truck market driven by military and outdoor buyers. We tune inventory pages and Google Ads toward those exact searches.",
      },
      {
        city: "Pueblo",
        body: "BHPH and credit-challenged buyers are huge in southern Colorado. We build dedicated landing pages and intake forms for that audience.",
      },
      {
        city: "Denver Metro",
        body: "Specialty inventory (off-road, European, EV) wins in Denver. We surface niche segments instead of competing on commodity used-car keywords.",
      },
      {
        city: "Castle Rock & Parker",
        body: "Higher-income suburbs mean luxury preowned and certified inventory. We design buying experiences that match those expectations.",
      },
    ],
    faqs: [
      {
        q: "Can you integrate our inventory feed?",
        a: "Yes. We integrate with vAuto, Frazer, DealerCenter, Wayne Reaves, and most major DMS exports so inventory pages stay current automatically.",
      },
      {
        q: "Will you help us compete with Cars.com and AutoTrader?",
        a: "Yes — by ranking your own VDP (vehicle detail) pages organically and running tighter Google Ads than the listing portals. You keep the lead instead of paying for it.",
      },
      {
        q: "Do you handle BHPH (Buy-Here-Pay-Here) marketing?",
        a: "Yes. BHPH is a totally different buyer with different search behavior. We build dedicated landing pages, application flows, and Spanish-language pages where it makes sense.",
      },
    ],
    primaryCtaLabel: COMMON_CTAS.proposal,
  },
];

export const INDUSTRY_PAGE_SLUGS = INDUSTRY_PAGES.map((p) => p.slug);

export function getIndustryPage(slug: string): IndustryPage | undefined {
  return INDUSTRY_PAGES.find((p) => p.slug === slug);
}
